import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import getImageUrl from "../../../utils/imageUtils";
import api from "../../../utils/api/axiosInstance";
import PageSeo from "../../../components/common/PageSeo";
import { useToast } from "../../../contexts/ToastContext";
import PostCard from "../../../components/profile/PostCard";
import CreatePostModal from "../../../components/profile/CreatePostModal";
import PostComposerCard from "../../../components/profile/PostComposerCard";
import { FollowersFollowingModal } from "../../../components/profile/modals";
import ProfileBanner from "../../../components/profile/ProfileBanner";
import ProfileAvatar from "../../../components/profile/ProfileAvatar";
import profileAvatarStyles from "../../../styles/components/profile/ProfileAvatar.module.css";

import {
  FaUser,
  FaUserPlus,
  FaUserCheck,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaImages,
  FaPaperPlane,
  FaEnvelope,
  FaBan,
  FaUnlock,
  FaEllipsisH,
  FaLink,
  FaClock,
  FaStar,
  FaTools,
  FaTh,
  FaList,
  FaBox,
  FaShoppingBag,
} from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import { usePublicPages } from "../../../contexts/PublicPagesContext";
import styles from "../../../styles/components/profile/PublicUserProfile.module.css";
import postStyles from "../../../styles/components/profile/PostsSection.module.css";

const PublicUserProfile = ({
  user,
  stats,
  trustIndicators,
  impact,
  isFollowing,
  hasPendingRequest,
  isOwnProfile,
  currentUser,
  followLoading,
  onFollow,
  onFollowersClick,
  onFollowingClick,
  showFollowers,
  showFollowing,
  followersList,
  followingList,
  loadingFollowers,
  loadingFollowing,
  onCloseFollowers,
  onCloseFollowing,
  expertiseScores,
  publicLists,
  badges,
  shoppingStyle,
  activeSection = "full",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { isPageEnabled } = usePublicPages();
  const feedEnabled = isPageEnabled("feed");
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createPostInitialType, setCreatePostInitialType] = useState("standard");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [localUser, setLocalUser] = useState(user);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [profileTab, setProfileTab] = useState(
    activeSection === "setup" ? "setup" : "posts",
  );
  const [setupView, setSetupView] = useState("grid");
  const dropdownRef = useRef(null);
  const postsRequestIdRef = useRef(0);

  // Fetch public setup for this user
  const { data: setupData } = useQuery({
    queryKey: ["publicSetup", user?._id],
    queryFn: async () => {
      const res = await api.get(`/setup/user/${user._id}`);
      return res.data;
    },
    enabled: !!user?._id,
    staleTime: 60_000,
  });
  const setupGrouped = useMemo(() => {
    const grouped = setupData?.grouped || {};
    const visible = {};
    for (const [cat, items] of Object.entries(grouped)) {
      const withProduct = (items || []).filter((item) => item?.product?._id);
      if (withProduct.length) visible[cat] = withProduct;
    }
    return visible;
  }, [setupData]);
  const setupCategoryNames = Object.keys(setupGrouped).sort();
  const setupTotal = useMemo(
    () => Object.values(setupGrouped).reduce((count, items) => count + items.length, 0),
    [setupGrouped],
  );

  const STATUS_LABEL = { own: "Own It", wishlist: "Wishlist", sold: "Sold" };

  // Update local user when prop changes
  useEffect(() => {
    setLocalUser(user);
  }, [user]);

  useEffect(() => {
    if (!feedEnabled) {
      setProfileTab("setup");
      return;
    }
    if (activeSection === "setup") setProfileTab("setup");
    if (activeSection === "posts") setProfileTab("posts");
  }, [activeSection, feedEnabled]);

  const showHeader =
    activeSection === "full" || activeSection === "header";
  const showPostsContent =
    feedEnabled &&
    (activeSection === "full" || activeSection === "posts");
  const showSetupContent =
    activeSection === "full" || activeSection === "setup";
  const useRouteTabs = activeSection !== "full";

  // Check if user is blocked
  useEffect(() => {
    let cancelled = false;

    const checkBlockStatus = async () => {
      if (!currentUser || isOwnProfile || !user?._id) return;

      try {
        const res = await api.get("/users/me/blocked");
        if (cancelled) return;

        const blockedUsers = res.data.blockedUsers || [];
        const blocked = blockedUsers.some(
          (blockedUser) => blockedUser._id === user._id,
        );
        setIsBlocked(blocked);
      } catch (err) {
        if (!cancelled) {
          console.error("Error checking block status:", err);
        }
      }
    };

    checkBlockStatus();
    return () => {
      cancelled = true;
    };
  }, [currentUser, user?._id, isOwnProfile]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOverflowMenu(false);
      }
    };

    if (showOverflowMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOverflowMenu]);

  const handleBannerUpdate = (newBanner) => {
    setLocalUser((prev) => ({
      ...prev,
      banner: newBanner
        ? `${newBanner}${newBanner.includes("?") ? "&" : "?"}t=${Date.now()}`
        : "",
    }));
  };

  const handleAvatarUpdate = (newAvatar) => {
    setLocalUser((prev) => ({
      ...prev,
      avatar: newAvatar
        ? `${newAvatar}${newAvatar.includes("?") ? "&" : "?"}t=${Date.now()}`
        : "",
    }));
  };

  const fetchPosts = useCallback(
    async (pageNum = 1, userId = null) => {
      const targetUserId =
        userId ||
        (isOwnProfile ? currentUser?.id || currentUser?._id : user?._id);
      const requestId = ++postsRequestIdRef.current;

      try {
        setLoadingPosts(true);
        const res = await api.get(`/posts/user/${targetUserId}`, {
          params: { page: pageNum, limit: 12 },
        });
        if (requestId !== postsRequestIdRef.current) return;

        const postsData = res.data.posts || [];
        if (pageNum === 1) {
          setPosts(postsData);
        } else {
          setPosts((prev) => [...prev, ...postsData]);
        }
        setHasMore(res.data.pagination.page < res.data.pagination.pages);
      } catch (err) {
        if (requestId !== postsRequestIdRef.current) return;
        console.error("Error fetching posts:", err);
      } finally {
        if (requestId === postsRequestIdRef.current) {
          setLoadingPosts(false);
        }
      }
    },
    [currentUser?.id, currentUser?._id, isOwnProfile, user?._id],
  );

  // Fetch posts on mount or when user changes
  useEffect(() => {
    if (!feedEnabled) {
      setPosts([]);
      setLoadingPosts(false);
      setHasMore(false);
      return;
    }
    const userId = isOwnProfile
      ? currentUser?.id || currentUser?._id
      : user?._id;
    if (userId) {
      setPage(1);
      setPosts([]);
      fetchPosts(1, userId);
    }
  }, [
    user?._id,
    currentUser?.id,
    currentUser?._id,
    isOwnProfile,
    fetchPosts,
    feedEnabled,
  ]);

  const loadMorePosts = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage);
  };

  const handlePostCreated = (newPost) => {
    setPosts((prev) => [newPost, ...prev]);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => String(p._id) !== String(postId)));
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) =>
      prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)),
    );
  };

  const handleOpenPost = (postId, event) => {
    const target = event.target;
    const tagName = target.tagName.toLowerCase();

    // Don't navigate when interacting with form / interactive controls
    const interactiveTags = [
      "button",
      "a",
      "input",
      "textarea",
      "select",
      "label",
    ];

    if (
      interactiveTags.includes(tagName) ||
      target.closest("button, a, input, textarea, select, label")
    ) {
      return;
    }

    const fromPath = `${location.pathname}${location.search}`;
    navigate(`/post/${postId}`, { state: { from: fromPath } });
  };

  const handleLikeUpdate = (postId, isLiked, likeCount) => {
    setPosts((prev) =>
      prev.map((p) =>
        p._id === postId
          ? {
              ...p,
              isLiked,
              likeCount,
              likes: isLiked
                ? [...(p.likes || []), currentUser?.id || currentUser?._id]
                : (p.likes || []).filter(
                    (id) => id !== (currentUser?.id || currentUser?._id),
                  ),
            }
          : p,
      ),
    );
  };

  const handleMessageClick = () => {
    // Only allow messaging when viewing someone else's profile and logged in
    if (!currentUser || isOwnProfile) return;

    const ownId = currentUser.id || currentUser._id;
    navigate(`/user/${ownId}/messages`, {
      state: {
        startConversationWith: {
          _id: localUser._id,
          firstName: localUser.firstName,
          lastName: localUser.lastName,
          username: localUser.username,
          avatar: localUser.avatar,
        },
      },
    });
  };

  const handleBlockToggle = async () => {
    if (!currentUser || isOwnProfile || blockLoading) return;

    try {
      setBlockLoading(true);
      setShowOverflowMenu(false);

      if (isBlocked) {
        // Unblock user
        await api.delete(`/users/${localUser._id}/block`);
        setIsBlocked(false);
      } else {
        // Block user
        await api.post(`/users/${localUser._id}/block`);
        setIsBlocked(true);
      }
    } catch (err) {
      console.error("Error toggling block status:", err);
      showToast({
        message: err.response?.data?.message || "Failed to update block status",
        type: "error",
      });
    } finally {
      setBlockLoading(false);
    }
  };

  const handleCopyProfileLink = () => {
    const profileUrl = `${window.location.origin}/user/${localUser._id || localUser.id}`;
    navigator.clipboard
      .writeText(profileUrl)
      .then(() => {
        showToast({ message: "Profile link copied!", type: "success" });
        setShowOverflowMenu(false);
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        showToast({ message: "Failed to copy link", type: "error" });
      });
  };

  const headerBlock = (
    <>
      {/* Profile Banner */}
      <ProfileBanner
        user={localUser}
        onUpdated={handleBannerUpdate}
        isOwnProfile={isOwnProfile}
      />

      {/* Profile Header */}
      <div className={styles.profileHeader}>
        <div className={styles.profileHeaderTop}>
          {/* Left Side: Avatar + Info */}
          <div className={styles.leftSection}>
            {/* Avatar */}
            <div className={styles.avatarSection}>
              {isOwnProfile ? (
                <ProfileAvatar
                  user={localUser}
                  onUpdated={handleAvatarUpdate}
                />
              ) : localUser.avatar ? (
                <div className={profileAvatarStyles.preview}>
                  <div className={profileAvatarStyles.avatarRing}>
                    <img
                      src={getImageUrl(localUser.avatar)}
                      alt={`${localUser.firstName} ${localUser.lastName}`}
                      className={profileAvatarStyles.avatarImage}
                    />
                  </div>
                </div>
              ) : (
                <div className={profileAvatarStyles.avatarPlaceholderFrame}>
                  <FaUser size={40} />
                </div>
              )}
            </div>

            {/* Info Below Avatar */}
            <div className={styles.leftInfo}>
              <h1 className={styles.name}>
                {localUser.firstName} {localUser.lastName}
              </h1>

              {localUser.username && (
                <p className={styles.username}>@{localUser.username}</p>
              )}

              {/* Action buttons */}
              {!isOwnProfile && currentUser && (
                <div className={styles.headerActions}>
                  <button
                    className={`${styles.followButtonHeader} ${
                      isFollowing ? styles.following : ""
                    } ${hasPendingRequest ? styles.pendingRequest : ""}`}
                    onClick={onFollow}
                    disabled={followLoading}
                  >
                    {isFollowing ? (
                      <>
                        <FaUserCheck /> Following
                      </>
                    ) : hasPendingRequest ? (
                      <>
                        <FaClock /> Request Sent
                      </>
                    ) : (
                      <>
                        <FaUserPlus /> Follow
                      </>
                    )}
                  </button>
                  <button
                    className={styles.messageButtonHeader}
                    onClick={handleMessageClick}
                    type="button"
                  >
                    <FaPaperPlane />
                    Message
                  </button>
                  <div className={styles.overflowMenuWrapper}>
                    <button
                      className={styles.overflowButton}
                      onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                      type="button"
                      title="More options"
                    >
                      <FaEllipsisH />
                    </button>
                    {showOverflowMenu && (
                      <div
                        className={styles.overflowDropdown}
                        ref={dropdownRef}
                      >
                        <button
                          onClick={handleBlockToggle}
                          disabled={blockLoading}
                          className={isBlocked ? styles.unblockOption : ""}
                        >
                          {isBlocked ? (
                            <>
                              <FaUnlock /> Unblock
                            </>
                          ) : (
                            <>
                              <FaBan /> Block
                            </>
                          )}
                        </button>
                        <button onClick={handleCopyProfileLink}>
                          <FaLink /> Copy Link
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Content Container: Center, Right, and Meta */}
          <div className={styles.rightContentContainer}>
            {/* Center: Bio & Meta */}

            {/* Right: Stats */}
            <div className={styles.rightSection}>
              <div className={styles.statsGrid}>
                <div
                  className={`${styles.statBlock} ${styles.clickableStat}`}
                  onClick={onFollowersClick}
                >
                  <span className={styles.statLabel}>Followers</span>
                  <span className={styles.statNumber}>
                    {stats.followers || 0}
                  </span>
                </div>
                <div
                  className={`${styles.statBlock} ${styles.clickableStat}`}
                  onClick={onFollowingClick}
                >
                  <span className={styles.statLabel}>Following</span>
                  <span className={styles.statNumber}>
                    {stats.following || 0}
                  </span>
                </div>
                {feedEnabled && (
                  <div className={styles.statBlock}>
                    <span className={styles.statLabel}>Posts</span>
                    <span className={styles.statNumber}>{stats.posts || 0}</span>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.centerSection}>
              {shoppingStyle?.label && (
                <p className={styles.shoppingStyle}>
                  <FaShoppingBag className={styles.shoppingStyleIcon} aria-hidden />
                  <span>{shoppingStyle.label}</span>
                </p>
              )}
              {localUser.profile?.bio && (
                <>
                  <p className={styles.bio}>{localUser.profile.bio}</p>
                </>
              )}
            </div>

            {/* Profile Meta */}
            <div className={styles.profileMeta}>
              {localUser.profile?.location && (
                <div className={styles.metaItem}>
                  <FaMapMarkerAlt className={styles.metaIcon} />
                  <span>{localUser.profile.location}</span>
                </div>
              )}
              {localUser.createdAt && (
                <div className={styles.metaItem}>
                  <FaCalendarAlt className={styles.metaIcon} />
                  <span>
                    Joined{" "}
                    {new Date(localUser.createdAt).toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
              )}
              {localUser.profile?.showEmail === true && localUser.email && (
                <div className={styles.metaItem}>
                  <FaEnvelope className={styles.metaIcon} />
                  <span>{localUser.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Expertise Scores */}
        {expertiseScores && Object.keys(expertiseScores).length > 0 && (
          <div className={styles.expertiseSection}>
            <h3 className={styles.sectionTitle}>Category Expertise</h3>
            <div className={styles.expertiseGrid}>
              {Object.entries(expertiseScores).map(([category, data]) => (
                <div key={category} className={styles.expertiseCard}>
                  <span className={styles.expertiseCategoryName}>{category}</span>
                  <span className={`${styles.expertiseTierBadge} ${styles[`tier_${data.tier}`]}`}>
                    {data.tier}
                  </span>
                  <span className={styles.expertiseMeta}>
                    {data.reviewCount} review{data.reviewCount !== 1 ? "s" : ""} · {data.helpfulVotes} helpful
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Followers Modal */}
      <FollowersFollowingModal
        isOpen={showFollowers}
        type="followers"
        onClose={onCloseFollowers}
        list={followersList}
        loading={loadingFollowers}
      />

      {/* Following Modal */}
      <FollowersFollowingModal
        isOpen={showFollowing}
        type="following"
        onClose={onCloseFollowing}
        list={followingList}
        loading={loadingFollowing}
      />
    </>
  );

  const postsAndSetupBlock = (
    <div className={postStyles.postsSection}>
        {(!useRouteTabs ||
          (showSetupContent && profileTab === "setup")) && (
        <div className={postStyles.postsHeader}>
          {!useRouteTabs && (
          <div style={{ display: "flex", gap: "4px" }}>
            {feedEnabled && (
            <button
              type="button"
              onClick={() => setProfileTab("posts")}
              style={{
                background: profileTab === "posts" ? "var(--color-primary)" : "none",
                color: profileTab === "posts" ? "#fff" : "var(--color-text-secondary)",
                border: "1px solid " + (profileTab === "posts" ? "var(--color-primary)" : "var(--color-border)"),
                borderRadius: "20px",
                padding: "5px 14px",
                cursor: "pointer",
                fontFamily: "var(--font-family)",
                fontSize: "0.85rem",
                fontWeight: profileTab === "posts" ? "700" : "500",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <FaImages size={12} /> Posts
            </button>
            )}
            <button
              type="button"
              onClick={() => setProfileTab("setup")}
              style={{
                background: profileTab === "setup" ? "var(--color-primary)" : "none",
                color: profileTab === "setup" ? "#fff" : "var(--color-text-secondary)",
                border: "1px solid " + (profileTab === "setup" ? "var(--color-primary)" : "var(--color-border)"),
                borderRadius: "20px",
                padding: "5px 14px",
                cursor: "pointer",
                fontFamily: "var(--font-family)",
                fontSize: "0.85rem",
                fontWeight: profileTab === "setup" ? "700" : "500",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <FaTools size={12} /> Setup {setupTotal > 0 && `(${setupTotal})`}
            </button>
          </div>
          )}
          {showSetupContent && profileTab === "setup" && (
            <div style={{ display: "flex", gap: "4px", marginLeft: "auto" }}>
              <button type="button" onClick={() => setSetupView("grid")} style={{ background: setupView === "grid" ? "var(--color-bg-secondary)" : "none", border: "1px solid var(--color-border)", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", color: "var(--color-text-secondary)", fontFamily: "var(--font-family)", fontSize: "0.8rem" }}>
                <FaTh size={11} />
              </button>
              <button type="button" onClick={() => setSetupView("list")} style={{ background: setupView === "list" ? "var(--color-bg-secondary)" : "none", border: "1px solid var(--color-border)", borderRadius: "6px", padding: "4px 10px", cursor: "pointer", color: "var(--color-text-secondary)", fontFamily: "var(--font-family)", fontSize: "0.8rem" }}>
                <FaList size={11} />
              </button>
            </div>
          )}
        </div>
        )}

        {showPostsContent && profileTab === "posts" && isOwnProfile && currentUser && (
          <PostComposerCard
            user={currentUser}
            centered
            onCompose={(postType) => {
              setCreatePostInitialType(postType);
              setShowCreateModal(true);
            }}
          />
        )}

        {showSetupContent && profileTab === "setup" && (
          <div>
            {setupTotal === 0 ? (
              <div className={postStyles.emptyState}>
                <FaTools size={32} />
                <p>
                  {isOwnProfile
                    ? "You haven't added anything to your Setup yet. Visit a product page to get started."
                    : `${localUser.firstName} hasn't built their Setup yet.`}
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "8px" }}>
                {setupCategoryNames.map((cat) => (
                  <div key={cat} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <h3 style={{ margin: 0, fontFamily: "var(--font-family)", fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-primary)", display: "flex", alignItems: "center", gap: "8px" }}>
                      {cat}
                      <span style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)", background: "var(--color-bg-secondary)", border: "1px solid var(--color-border)", borderRadius: "20px", padding: "1px 8px", fontWeight: "500" }}>
                        {setupGrouped[cat].length}
                      </span>
                    </h3>
                    {setupView === "grid" ? (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: "12px" }}>
                        {setupGrouped[cat].map((item) => {
                          const src = Array.isArray(item.product?.images) && item.product.images.length
                            ? getImageUrl(item.product.images[0]) : null;
                          const statusBg = item.status === "own" ? "rgba(16,185,129,0.12)" : item.status === "wishlist" ? "rgba(99,102,241,0.12)" : "rgba(0,0,0,0.06)";
                          const statusColor = item.status === "own" ? "#059669" : item.status === "wishlist" ? "#3B7EF6" : "var(--color-text-secondary)";
                          return (
                            <a key={item._id} href={`/product/${item.product._id}`} style={{ textDecoration: "none", background: "var(--color-bg-secondary)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", overflow: "hidden", display: "block", transition: "box-shadow 0.18s, border-color 0.18s" }}
                              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)"; e.currentTarget.style.borderColor = "var(--color-primary)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--color-border)"; }}
                            >
                              {src ? (
                                <img src={src} alt={item.product.name} style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover", display: "block" }} />
                              ) : (
                                <div style={{ width: "100%", aspectRatio: "4/3", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--color-bg-tertiary, #f0f0f2)", color: "var(--color-border)" }}>
                                  <FaBox size={24} />
                                </div>
                              )}
                              <div style={{ padding: "10px" }}>
                                <p style={{ margin: "0 0 2px", fontSize: "0.84rem", fontWeight: "600", color: "var(--color-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "var(--font-family)" }}>{item.product.name}</p>
                                {item.product.brand?.name && <p style={{ margin: "0 0 6px", fontSize: "0.74rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-family)" }}>{item.product.brand.name}</p>}
                                <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap", marginBottom: item.note ? "6px" : "0" }}>
                                  <span style={{ fontSize: "0.7rem", fontWeight: "600", padding: "2px 7px", borderRadius: "20px", fontFamily: "var(--font-family)", background: statusBg, color: statusColor }}>
                                    {STATUS_LABEL[item.status] || item.status}
                                  </span>
                                  {item.rating > 0 && (
                                    <span style={{ display: "flex", alignItems: "center", gap: "2px", color: "#f59e0b", fontSize: "0.72rem" }}>
                                      {Array.from({ length: item.rating }).map((_, i) => <FaStar key={i} size={9} />)}
                                    </span>
                                  )}
                                </div>
                                {item.note && (
                                  <p style={{ margin: "0", fontSize: "0.76rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-family)", fontStyle: "italic", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    "{item.note}"
                                  </p>
                                )}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                        {setupGrouped[cat].map((item) => {
                          const src = Array.isArray(item.product?.images) && item.product.images.length
                            ? getImageUrl(item.product.images[0]) : null;
                          const statusBg = item.status === "own" ? "rgba(16,185,129,0.12)" : item.status === "wishlist" ? "rgba(99,102,241,0.12)" : "rgba(0,0,0,0.06)";
                          const statusColor = item.status === "own" ? "#059669" : item.status === "wishlist" ? "#3B7EF6" : "var(--color-text-secondary)";
                          return (
                            <a key={item._id} href={`/product/${item.product._id}`} style={{ textDecoration: "none", display: "flex", alignItems: "flex-start", gap: "12px", background: "var(--color-bg-secondary)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "12px 14px", transition: "border-color 0.18s" }}
                              onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--color-primary)"}
                              onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--color-border)"}
                            >
                              {src ? (
                                <img src={src} alt={item.product.name} style={{ width: "52px", height: "52px", objectFit: "cover", borderRadius: "var(--radius-sm)", flexShrink: 0, border: "1px solid var(--color-border)" }} />
                              ) : (
                                <div style={{ width: "52px", height: "52px", borderRadius: "var(--radius-sm)", background: "var(--color-bg-tertiary, #f0f0f2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "var(--color-border)", border: "1px solid var(--color-border)" }}>
                                  <FaBox size={18} />
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: "0 0 2px", fontSize: "0.9rem", fontWeight: "600", color: "var(--color-text-primary)", fontFamily: "var(--font-family)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.product.name}</p>
                                {item.product.brand?.name && <p style={{ margin: "0 0 5px", fontSize: "0.78rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-family)" }}>{item.product.brand.name}</p>}
                                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: item.note ? "5px" : "0" }}>
                                  <span style={{ fontSize: "0.71rem", fontWeight: "600", padding: "2px 8px", borderRadius: "20px", fontFamily: "var(--font-family)", background: statusBg, color: statusColor }}>
                                    {STATUS_LABEL[item.status] || item.status}
                                  </span>
                                  {item.rating > 0 && (
                                    <span style={{ display: "flex", alignItems: "center", gap: "2px", color: "#f59e0b", fontSize: "0.72rem" }}>
                                      {Array.from({ length: item.rating }).map((_, i) => <FaStar key={i} size={9} />)}
                                    </span>
                                  )}
                                  {item.ownedSince && (
                                    <span style={{ fontSize: "0.71rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-family)" }}>
                                      Since {new Date(item.ownedSince).getFullYear()}
                                    </span>
                                  )}
                                </div>
                                {item.note && (
                                  <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-secondary)", fontFamily: "var(--font-family)", fontStyle: "italic", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                                    "{item.note}"
                                  </p>
                                )}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {showPostsContent && profileTab === "posts" && (loadingPosts ? (
          <div className={postStyles.postsGrid}>
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className={postStyles.postSkeleton}>
                <div className={postStyles.skeletonHeader}>
                  <div className={postStyles.skeletonAvatar}></div>
                  <div className={postStyles.skeletonHeaderText}>
                    <div className={postStyles.skeletonName}></div>
                    <div className={postStyles.skeletonDate}></div>
                  </div>
                </div>
                <div className={postStyles.skeletonContent}></div>
                <div className={postStyles.skeletonActions}>
                  <div className={postStyles.skeletonButton}></div>
                  <div className={postStyles.skeletonButton}></div>
                </div>
              </div>
            ))}
          </div>
        ) : posts.length > 0 ? (
          <>
            <div className={postStyles.postsGrid}>
              {posts.map((post) => (
                <div
                  key={post._id}
                  className={postStyles.postClickableWrapper}
                  onClick={(e) => handleOpenPost(post._id, e)}
                >
                  <PostCard
                    post={post}
                    onDelete={handlePostDeleted}
                    onLikeUpdate={handleLikeUpdate}
                    onPostUpdated={handlePostUpdated}
                    commentAction="navigate"
                  />
                </div>
              ))}
            </div>
            {hasMore && (
              <button
                className={postStyles.loadMoreButton}
                onClick={loadMorePosts}
                disabled={loadingPosts}
              >
                {loadingPosts ? "Loading..." : "Load More"}
              </button>
            )}
          </>
        ) : (
          <div className={postStyles.emptyState}>
            <FaImages />
            <p>
              {isOwnProfile
                ? "You haven't created any posts yet. Share your products!"
                : `${localUser.firstName} hasn't created any posts yet.`}
            </p>
            {isOwnProfile && currentUser ? (
              <button
                className={postStyles.createFirstPostButton}
                onClick={() => setShowCreateModal(true)}
              >
                Create Your First Post
              </button>
            ) : (
              !isOwnProfile &&
              currentUser &&
              !isFollowing && (
                <button
                  className={postStyles.followForUpdatesButton}
                  onClick={onFollow}
                  disabled={followLoading || hasPendingRequest}
                >
                  {hasPendingRequest ? (
                    <>
                      <FaClock /> Request Sent
                    </>
                  ) : (
                    <>
                      <FaUserPlus /> Follow to see when they post
                    </>
                  )}
                </button>
              )
            )}
          </div>
        ))}
      </div>
  );

  const profileName = localUser
    ? [localUser.firstName, localUser.lastName].filter(Boolean).join(" ")
    : "User";
  const profileId = localUser?._id || localUser?.id;
  const profilePath = profileId ? `/user/${profileId}` : "/";

  return (
    <div className={styles.profileContainer}>
      {localUser && (
        <PageSeo
          title={
            localUser.username
              ? `${profileName} (@${localUser.username})`
              : profileName
          }
          description={
            localUser.bio
              ? String(localUser.bio).slice(0, 160)
              : `View ${profileName}'s profile on Simpary.`
          }
          pathname={profilePath}
          imagePath={localUser.avatar || undefined}
        />
      )}
      {showHeader && headerBlock}
      {(showPostsContent || showSetupContent) && postsAndSetupBlock}
      {feedEnabled && (
        <CreatePostModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setCreatePostInitialType("standard");
          }}
          onPostCreated={handlePostCreated}
          initialPostType={createPostInitialType}
        />
      )}
    </div>
  );
};

export default PublicUserProfile;
